/**
 * ConversationContextModal Component
 *
 * A modal dialog for displaying conversation context around a selected message.
 */

import { toClassificationDialogData } from '@content-search/adapters';
import type { ContentCardItem, ContentSearchItem } from '@content-search/domain';
import { ClassificationDialog, ErrorBanner, Illustration, IllustrationLayout, IllustrationType } from '@core/components';
import type { DomainError } from '@core/domain';
import { Dialog, Shimmer, Reading, Icon, IconSize } from '@soteria/design-system-ve';
import { useEffect, useRef, useState } from 'react';

import * as styles from './ConversationContextModal.css';
import { ModalMessageCard, ModalLoadMoreButton } from './ModalMessageCard';
import { convertToContentCardItem } from './helpers';
import { useConversationContextModal } from './useConversationContextModal';

export interface ConversationContextModalProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
  subjectId: string;
  topicName: string;
  metadata: Record<string, unknown>;
  selectedMessage?: ContentCardItem;
  mediaServiceUrl?: string;
  /** When true, a content search is still running to resolve metadata — show shimmer */
  isSearchingContent?: boolean;
  /** Errors from the initial content search used to resolve metadata */
  contentSearchErrors?: DomainError[] | null;
}

/** Custom header component */
function ConversationContextHeader() {
  return (
    <div className={styles.headerContainer}>
      <div className={styles.headerIcon}>
        <Icon icon={Reading} iconSize={IconSize.Small} />
      </div>
      <h2 className={styles.headerTitle}>Message Context</h2>
    </div>
  );
}

/** Metadata filters section */
function MetadataFilters({ metadata }: { metadata: Record<string, unknown> }) {
  return (
    <div className={styles.appliedFiltersSection}>
      <div className={styles.metadataFiltersWrapper}>
        {Object.entries(metadata).map(([key, value]) => {
          if (value === null || value === undefined || value === '') return null;
          const displayValue = String(value);
          const isNoneValue = displayValue.toLowerCase() === 'none';

          return (
            <div key={`metadata-${key}-${value}`} className={styles.metadataItem}>
              <span className={styles.metadataLabel}>{key.charAt(0).toUpperCase() + key.slice(1)}:</span>
              {isNoneValue ? <span className={styles.emptyBadgeText}>None</span> : <span className={styles.metadataValue}>{displayValue}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Loading shimmer display */
function ConversationShimmer() {
  return (
    <div className={styles.shimmerContainer}>
      <div className={styles.conversationWrapper}>
        <Shimmer className={styles.shimmerSmall} />
        <Shimmer className={styles.shimmerLarge} />
        <Shimmer className={styles.shimmerMedium} />
      </div>
    </div>
  );
}

/** Empty state component */
function EmptyState() {
  return (
    <div className={styles.emptyStateContainer}>
      <Illustration
        description="No messages could be found for this conversation context."
        layout={IllustrationLayout.HORIZONTAL}
        title="No conversation found"
        type={IllustrationType.EMPTY}
      />
    </div>
  );
}

/** Loading shimmer for load more */
function LoadMoreShimmer() {
  return (
    <div className={styles.shimmerContainer}>
      <Shimmer className={styles.shimmerSmall} />
    </div>
  );
}

/** Get alignment based on whether this user matches the selected message user */
function getMessageAlignment(messageUserId?: string, selectedUserId?: string): 'left' | 'right' {
  if (!selectedUserId || !messageUserId) return 'left';
  if (selectedUserId === 'Unknown' || messageUserId === 'Unknown') return 'left';

  return selectedUserId === messageUserId ? 'right' : 'left';
}

interface MessageItemProps {
  message: ContentSearchItem;
  selectedMessage?: ContentCardItem;
  mediaServiceUrl?: string;
}

/** Check if message is the originally selected message */
function isSelectedMessage(messageCardItem: ContentCardItem, selectedMessage?: ContentCardItem): boolean {
  if (!selectedMessage) return false;
  const messageSubjectId = messageCardItem.subjectId || messageCardItem.id;
  const originalSelectedId = selectedMessage.subjectId || selectedMessage.id;

  return messageSubjectId === originalSelectedId;
}

/** Individual message item in conversation — rendered as a chat bubble */
function MessageItem({ message, selectedMessage, mediaServiceUrl }: MessageItemProps) {
  const messageCardItem = convertToContentCardItem(message);
  const isOriginalSelectedMessage = isSelectedMessage(messageCardItem, selectedMessage);
  const alignment = getMessageAlignment(messageCardItem.subjectOwner?.userId, selectedMessage?.subjectOwner?.userId);

  // Inherit classification from the original selected message when the conversation
  // context API doesn't return classification data for the same message.
  if (isOriginalSelectedMessage && selectedMessage?.classificationResult === 'rejected') {
    messageCardItem.classificationResult = 'rejected';
  }

  const [showClassification, setShowClassification] = useState(false);
  const isSafeContent = messageCardItem.classificationResult !== 'rejected';
  const [isContentBlurred, setIsContentBlurred] = useState(!isSafeContent);

  // Use selectedMessage for the classification dialog when this is the selected message.
  // The selectedMessage has full classification data (confidence, processing time, policies,
  // labels) from the content search API, while the contextual-messages API response
  // only has minimal classification info (0% confidence, 0ms, no policies).
  const classificationSource = isOriginalSelectedMessage && selectedMessage ? selectedMessage : messageCardItem;
  const dialogData = toClassificationDialogData(classificationSource);

  return (
    <>
      <ModalMessageCard
        alignment={alignment}
        isOriginalSelection={isOriginalSelectedMessage}
        item={messageCardItem}
        mediaServiceUrl={mediaServiceUrl}
        onClick={() => setShowClassification(true)}
      />
      <ClassificationDialog
        data={dialogData}
        isContentBlurred={isContentBlurred}
        isOpen={showClassification}
        onClose={() => setShowClassification(false)}
        onToggleBlur={() => setIsContentBlurred(prev => !prev)}
      />
    </>
  );
}

/** ConversationContextModal component */
// eslint-disable-next-line complexity -- orchestrates multiple UI states (loading/empty/error/paginated thread)
export function ConversationContextModal({
  isOpen,
  onClose,
  communityId,
  subjectId,
  topicName,
  metadata,
  selectedMessage,
  mediaServiceUrl,
  isSearchingContent = false,
  contentSearchErrors,
}: ConversationContextModalProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    isLoading,
    errors,
    isMetadataValid,
    memoizedMetadata,
    targetMessage,
    conversationMessages,
    loadingDirection,
    canLoadMore,
    hasScrolledToTarget,
    setHasScrolledToTarget,
    handleLoadMoreAbove,
    handleLoadMoreBelow,
  } = useConversationContextModal({
    isOpen,
    communityId,
    subjectId,
    topicName,
    metadata,
    selectedMessage,
  });

  // Scroll to selected message
  useEffect(() => {
    if (targetMessage && conversationMessages.length > 0 && !hasScrolledToTarget && isOpen) {
      const timeoutId = setTimeout(() => {
        const selectedElement = scrollContainerRef.current?.querySelector(`[data-message-id="${targetMessage.id}"]`);

        if (selectedElement) {
          const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

          (selectedElement as HTMLElement).scrollIntoView({
            behavior: prefersReducedMotion ? 'instant' : 'smooth',
            block: 'center',
            inline: 'nearest',
          });
          setHasScrolledToTarget(true);
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [targetMessage, conversationMessages, hasScrolledToTarget, isOpen, setHasScrolledToTarget]);

  return (
    <Dialog showCloseButton customHeader={<ConversationContextHeader />} isOpen={isOpen} size="xxl" onClose={onClose}>
      <div data-conversation-context-modal className={styles.modalContent}>
        {isMetadataValid && <MetadataFilters metadata={memoizedMetadata} />}
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error fetching conversation context:" />}
        {contentSearchErrors && contentSearchErrors.length > 0 && <ErrorBanner errors={contentSearchErrors} title="There was an error searching content:" />}

        {(isLoading || isSearchingContent) && conversationMessages.length === 0 ? (
          <ConversationShimmer />
        ) : conversationMessages.length === 0 && !isSearchingContent ? (
          <EmptyState />
        ) : (
          <div ref={scrollContainerRef} className={styles.messagesContainer}>
            <div className={styles.conversationWrapper}>
              {canLoadMore.above && !loadingDirection && <ModalLoadMoreButton direction="up" isLoading={false} onClick={handleLoadMoreAbove} />}
              {loadingDirection === 'above' && <LoadMoreShimmer />}

              {conversationMessages.map(message => (
                <MessageItem key={message.subjectId || message.id} mediaServiceUrl={mediaServiceUrl} message={message} selectedMessage={selectedMessage} />
              ))}

              {loadingDirection === 'below' && <LoadMoreShimmer />}
              {canLoadMore.below && !loadingDirection && <ModalLoadMoreButton direction="down" isLoading={false} onClick={handleLoadMoreBelow} />}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
